import LAYOUT from './layout.json';

/**
 * Mines drawn from its real-3D render (scripts/art/render/mines.html): the tray, a covered
 * tile, two gems (each a strip of spin frames), the bomb and their shadows, all seen by one
 * orthographic camera. From that camera every cell looks the same, so a layer rendered on
 * the middle cell is the same picture on any cell, moved by that cell's offset; the board
 * places every piece by its box, in percent of the tray, and the pieces compose back into
 * the picture they were cut from.
 */

export type Box = [number, number, number, number];
const BOXES = LAYOUT.boxes as unknown as Record<string, Box>;
const CELLS = LAYOUT.scene.cells as unknown as [number, number][];
const MIDDLE = 12;

export const GEM_FRAMES: number = LAYOUT.scene.gemFrames;

/** Every piece the board draws: the rendered layers, plus three of light. */
export const MINES_PIECES = [
  'board',
  'tile',
  'tileshadow',
  'gem-pink',
  'gem-white',
  'gemshadow',
  'bomb',
  'bombshadow',
  'sparkle',
  'glow',
  'blast',
] as const;
export type MinesPiece = (typeof MINES_PIECES)[number];

export type GemKind = 'pink' | 'white';

/** The card's mix of pink and white stones, scattered so no row or column repeats one. */
export function gemKind(cell: number): GemKind {
  return ((cell % 5) + 2 * Math.floor(cell / 5)) % 3 === 0 ? 'white' : 'pink';
}

/** A box as CSS percentages of another box: left, top, width, height. */
export interface PctBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

function pct(box: Box, of: Box): PctBox {
  const [x, y, w, h] = box;
  const [ox, oy, ow, oh] = of;
  return { left: ((x - ox) / ow) * 100, top: ((y - oy) / oh) * 100, width: (w / ow) * 100, height: (h / oh) * 100 };
}

/** The tray's box: the board element is this, and its aspect ratio. */
export const VIEW: Box = BOXES.board!;
export const VIEW_ASPECT = VIEW[2] / VIEW[3];

/** A middle-cell layer's box moved onto `cell`. */
export function boxAt(piece: string, cell: number): Box {
  const [x, y, w, h] = BOXES[piece]!;
  const [cx, cy] = CELLS[cell]!;
  const [mx, my] = CELLS[MIDDLE]!;
  return [x + cx - mx, y + cy - my, w, h];
}

/** Where a cell's tile (and its button) sits on the board. */
export function cellBox(cell: number): PctBox {
  return pct(boxAt('tile', cell), VIEW);
}

/** A piece's place inside its cell's tile box: the same on every cell. */
export function inCell(piece: string): PctBox {
  return pct(BOXES[piece]!, BOXES.tile!);
}

/** Where the lit fuse ends, in percent of the bomb's box. */
export function fuseTip(): { left: number; top: number } {
  const [x, y, w, h] = BOXES.bomb!;
  const [fx, fy] = LAYOUT.scene.fuseTip as unknown as [number, number];
  return { left: ((fx - x) / w) * 100, top: ((fy - y) / h) * 100 };
}

/** A cell's middle in percent of the board, for light that is not held by the cell. */
export function cellCentre(cell: number): { left: number; top: number } {
  const [x, y, w, h] = boxAt('tile', cell);
  return { left: ((x + w / 2 - VIEW[0]) / VIEW[2]) * 100, top: ((y + h / 2 - VIEW[1]) / VIEW[3]) * 100 };
}
